import { emptyRichText } from "@/plugins/html";
import { DatabaseObject, Metrics } from "./class";
import { FileLink } from "./file";
import type { FunctionalLocation } from "./functional-location";
import { User } from "./user";

export type Deleteable =
  | IndividualAssetManagementPlan
  | RACIRole
  | RACITask
  | RACIData
  | FLOCBoundary
  | RiskRegisterLink
  | WorkManagementMetricsLink
  | ReliabilityMetricsLink;

export type DeleteableKey =
  | "individual_asset_management_plan"
  | "raci_task"
  | "raci_role"
  | "raci_data"
  | "floc_boundary"
  | "risk_register_links"
  | "work_management_metrics_links"
  | "reliability_metrics_links";

export class AssetManagementPlanMasterMetrics extends Metrics {
  latest_revision_id: string | null;
}

export interface AssetManagementPlanMasterNewDetails {
  name: string;
}
export class AssetManagementPlanMaster extends DatabaseObject {
  static $route = "asset-management-plan-master";

  metrics: AssetManagementPlanMasterMetrics =
    new AssetManagementPlanMasterMetrics();

  name: string = "";

  asset_management_plans: AssetManagementPlan[];
  approval_stages: AssetManagementPlanMasterApprovalStage[] = [];

  latest_revision: AssetManagementPlan | null = null;
  latest_published_revision: AssetManagementPlan | null = null;

  get latest_revision_name() {
    return this.latest_revision?.revision ?? "";
  }

  get latest_revision_status() {
    return this.latest_revision?.status ?? "Draft";
  }

  get latest_published_revision_name() {
    return this.latest_published_revision?.revision ?? "";
  }

  get latest_published_timestamp() {
    return this.latest_published_revision?.published_timestamp ?? null;
  }

  static async newPlan(details: AssetManagementPlanMasterNewDetails) {
    return AssetManagementPlanMaster.fromAxios<AssetManagementPlanMaster>(
      await AssetManagementPlanMaster.$axios.$post<AssetManagementPlanMaster>(
        "asset-management-plan-master/new",
        details,
      ),
    );
  }

  async reorderStages() {
    this.approval_stages.forEach((stage, i) => {
      stage.order = i;
    });
    await AssetManagementPlanMaster.$axios.$put(
      "/asset-management-plan-master/reorder-stages",
      this.approval_stages.map((stage) => ({
        id: stage.id,
        order: stage.order,
      })),
    );
  }
}

export type AssetManagementPlanStatus =
  | "Draft"
  | "In Review"
  | "Published"
  | "Archived";

export class AssetManagementPlan extends DatabaseObject {
  static $route = "asset-management-plan";

  asset_management_plan_master_id: string;
  asset_management_plan_master: AssetManagementPlanMaster;

  // General
  status: AssetManagementPlanStatus = "Draft";
  revision: string;
  approvals: AssetManagementPlanApproval[] = [];

  // Metadata
  publisher_id: string | null = null;
  published_timestamp: Date | null = null;

  // Introduction;
  purpose: string;
  executive_summary: string;

  // RACI
  raci: string;
  raci_tasks: RACITask[] = [];
  raci_roles: RACIRole[] = [];
  raci_data: RACIData[] = [];

  // Scope & Boundary Limits
  context_general_information: string;
  floc_boundaries: FLOCBoundary[] = [];

  // Asset Information
  ownership_responsibility: string;
  asset_class_description: string;
  selection_procurement: string;
  risk_register: string;

  risk_register_links: RiskRegisterLink[] = [];

  // Asset Management Plan
  individual_asset_management_plans: IndividualAssetManagementPlan[] = [];

  // Service Levels & KPI's
  work_management_metrics: string;
  reliability_metrics: string;

  work_management_metrics_links: WorkManagementMetricsLink[] = [];
  reliability_metrics_links: ReliabilityMetricsLink[] = [];

  // Life Cycle Cost Modelling
  life_cycle_costs: string;
  replacement_value: string;
  budgets: string;
  sustaining_capital_budget: string;
  capital_projects_budget: string;
  life_cycle_cost_model: string;
  end_of_life: string;

  // Improvement Actions
  action_register: string;

  asset_management_plan_files: AssetManagementPlanFile[] = [];

  get status_color() {
    if (this.status === "In Review") return "blue";
    if (this.status === "Published") return "green";
    if (this.status === "Archived") return "red";
    return "";
  }

  get valid() {
    return (
      this.valid_section_introduction &&
      this.valid_section_raci &&
      this.valid_section_scope_boundary_limits &&
      this.valid_section_asset_information &&
      this.valid_section_asset_management_plan &&
      this.valid_section_service_levels_kpis &&
      this.valid_section_life_cycle_cost_modelling &&
      this.valid_section_improvement_actions
    );
  }

  get valid_section_introduction() {
    return (
      !emptyRichText(this.purpose) && !emptyRichText(this.executive_summary)
    );
  }

  get valid_section_raci() {
    return !emptyRichText(this.raci);
  }

  get valid_section_scope_boundary_limits() {
    return !emptyRichText(this.context_general_information);
  }

  get valid_section_asset_information() {
    return (
      !emptyRichText(this.ownership_responsibility) &&
      !emptyRichText(this.asset_class_description) &&
      !emptyRichText(this.selection_procurement) &&
      !emptyRichText(this.risk_register)
    );
  }

  get valid_section_asset_management_plan() {
    return this.individual_asset_management_plans.every((plan) => plan.valid);
  }

  get valid_section_service_levels_kpis() {
    return (
      !emptyRichText(this.work_management_metrics) &&
      !emptyRichText(this.reliability_metrics)
    );
  }

  get valid_section_life_cycle_cost_modelling() {
    return (
      !emptyRichText(this.life_cycle_costs) &&
      !emptyRichText(this.replacement_value) &&
      !emptyRichText(this.budgets) &&
      !emptyRichText(this.sustaining_capital_budget) &&
      !emptyRichText(this.capital_projects_budget) &&
      !emptyRichText(this.life_cycle_cost_model) &&
      !emptyRichText(this.end_of_life)
    );
  }

  get valid_section_improvement_actions() {
    return !emptyRichText(this.action_register);
  }

  get active_approval_stage() {
    for (const stage of this.asset_management_plan_master.approval_stages) {
      if (stage.status === "Pending") return stage;
    }
    return null;
  }

  async update(deleteIds: Record<DeleteableKey, string[]>) {
    if (!this.saved) {
      throw new Error("Cannot update an object that has not been created");
    }
    const object = AssetManagementPlan.fromAxios(
      await AssetManagementPlan.$axios.$put<AssetManagementPlan>(
        "/asset-management-plan/update",
        {
          plan: this,
          delete_ids: deleteIds,
        },
      ),
    );
    this.objectRefreshRelationship(object);
    this.fixRelationships();
  }

  fixRelationships() {
    for (const approval of this.approvals) {
      const stage = this.asset_management_plan_master.approval_stages.find(
        (s) => s.id === approval.stage_id,
      );
      if (stage) {
        stage.current_approval = approval;
      }
    }
  }
}

export class AssetManagementPlanMasterApprovalStage extends DatabaseObject {
  static $route = "asset-management-plan-master-approval-stage";

  order: number;
  asset_management_plan_master_id: string;

  asset_management_plan_master: AssetManagementPlanMaster;
  approvers: AssetManagementPlanMasterApprover[] = [];

  current_approval: AssetManagementPlanApproval | null = null;

  get status() {
    if (this.current_approval && this.current_approval.done) {
      return this.current_approval.rejected ? "Rejected" : "Approved";
    }
    return "Pending";
  }

  get status_tooltip() {
    if (this.current_approval?.name && this.status !== "Pending") {
      return `${
        this.current_approval.name
      } ${this.status.toLowerCase()} this revision.`;
    }
    return "";
  }

  get comment() {
    return this.current_approval?.comment || "";
  }

  formatApprovers(users: User[]) {
    const names: string[] = [];
    for (const approver of this.approvers) {
      const user = users.find((user) => user.id === approver.user_id);
      if (user) {
        names.push(user.name);
      }
    }
    return names.join(", ");
  }
}

export class AssetManagementPlanMasterApprover extends DatabaseObject {
  static $route = "asset-management-plan-master-approver";

  stage_id: string;
  user_id: string;

  stage: AssetManagementPlanMasterApprovalStage;
}

export class AssetManagementPlanApproval extends DatabaseObject {
  static $route = "asset-management-plan-approval";

  stage_id: string;
  asset_management_plan_id: string;
  timestamp: Date = new Date();
  name: string = "";
  comment: string = "";
  rejected: boolean = false;
  done: boolean = false;

  asset_management_plan: AssetManagementPlan;
}

export class AssetManagementPlanLink extends DatabaseObject {
  asset_management_plan_id: string;
  link: string;
}

export class RiskRegisterLink extends AssetManagementPlanLink {
  static $route = "risk-register-link";
}

export class WorkManagementMetricsLink extends AssetManagementPlanLink {
  static $route = "work-management-metrics-link";
}

export class ReliabilityMetricsLink extends AssetManagementPlanLink {
  static $route = "reliability-metrics-link";
}

type AssetManagementPlanFileLinkType = "AssetManagementFile";

export const assetManagementPlanFileTypes = [
  "purpose",
  "executive_summary",
  "raci",
  "context_general_information",
  "ownership_responsibility",
  "asset_class_description",
  "selection_procurement",
  "risk_register",
  "individual_asset_management_plans",
  "work_management_metrics",
  "reliability_metrics",
  "life_cycle_costs",
  "replacement_value",
  "budgets",
  "sustaining_capital_budget",
  "capital_projects_budget",
  "life_cycle_cost_model",
  "end_of_life",
  "action_register",
] as const;

export type AssetManagementPlanFileType =
  (typeof assetManagementPlanFileTypes)[number];
export class AssetManagementPlanFile extends FileLink {
  link_type: AssetManagementPlanFileLinkType = "AssetManagementFile";
  file_type: AssetManagementPlanFileType;
}

export class IndividualAssetManagementPlan extends DatabaseObject {
  static $route = "individual-asset-management-plan";

  asset_management_plan_id: string | null = null;
  asset_management_plan: AssetManagementPlan | null = null;

  management_consideration_factors: string = "";
  condition_monitoring: string = "";
  maintenance_defect_management: string = "";
  maintenance_plans: string = "";
  statutary_requirements: string = "";
  equipment_decomissioning_disposal: string = "";
  capital_projects: string = "";

  individual_asset_management_plan_files: IndividualAssetManagementPlanFile[] =
    [];

  get valid() {
    return (
      !emptyRichText(this.management_consideration_factors) &&
      !emptyRichText(this.condition_monitoring) &&
      !emptyRichText(this.maintenance_defect_management) &&
      !emptyRichText(this.maintenance_plans) &&
      !emptyRichText(this.statutary_requirements) &&
      !emptyRichText(this.equipment_decomissioning_disposal) &&
      !emptyRichText(this.capital_projects)
    );
  }
}

type IndividualAssetManagementPlanFileLinkType = "AssetManagementFile";

export const individualAssetManagementPlanFileTypes = [
  "purpose",
  "executive_summary",
  "raci",
  "context_general_information",
  "ownership_responsibility",
  "asset_class_description",
  "selection_procurement",
  "risk_register",
  "individual_asset_management_plans",
  "work_management_metrics",
  "reliability_metrics",
  "life_cycle_costs",
  "replacement_value",
  "budgets",
  "sustaining_capital_budget",
  "capital_projects_budget",
  "life_cycle_cost_model",
  "end_of_life",
  "action_register",
] as const;

export type IndividualAssetManagementPlanFileType =
  (typeof assetManagementPlanFileTypes)[number];
export class IndividualAssetManagementPlanFile extends FileLink {
  link_type: IndividualAssetManagementPlanFileLinkType = "AssetManagementFile";
  file_type: IndividualAssetManagementPlanFileType;
}

export class RACITask extends DatabaseObject {
  static $route = "raci-task";

  name: string = "";

  asset_management_plan_id: string | null = null;
  asset_management_plan: AssetManagementPlan | null = null;
}

export class RACIRole extends DatabaseObject {
  static $route = "raci-role";

  role_id: string | null = null;

  asset_management_plan_id: string | null = null;
  asset_management_plan: AssetManagementPlan | null = null;
}

export const raciValues = ["R", "A", "C", "I"] as const;
export type RACIValue = (typeof raciValues)[number];

export class RACIData extends DatabaseObject {
  static $route = "raci-data";

  asset_management_plan_id: string | null = null;
  asset_management_plan: AssetManagementPlan | null = null;

  value: RACIValue | null = null;
  raci_role_id: string;
  raci_task_id: string;
}

export class FLOCBoundary extends DatabaseObject {
  static $route = "asset-boundary";

  asset_management_plan_id: string | null = null;
  asset_management_plan: AssetManagementPlan | null = null;

  functional_location_id: string;
  functional_location: FunctionalLocation;
}
